import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ClienteDetalheMaster.module.css';
import { MdClose, MdCheckCircle, MdRadioButtonUnchecked } from 'react-icons/md';
import { fetchWithAuth } from '../../../shared/utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const PLANOS = [
  { valor: 'teste', rotulo: 'Teste grátis', ajuda: 'Sem cobrança até a data de fim do teste.' },
  { valor: 'mensal', rotulo: 'Mensalidade', ajuda: 'Valor fixo por mês.' },
  { valor: 'comissao', rotulo: 'Comissão (indisponível)', ajuda: 'Percentual do faturamento (pedidos entregues) do mês.', desabilitado: true },
  { valor: 'misto', rotulo: 'Mensalidade + comissão (indisponível)', ajuda: 'Os dois juntos.', desabilitado: true },
  { valor: 'isento', rotulo: 'Isento', ajuda: 'Parceiro/cortesia: não cobra.' }
];

// Plano do produto (o que o cliente pode usar) — diferente do plano de cobrança abaixo.
const PLANOS_PRODUTO = [
  { valor: 'start', rotulo: 'Start' },
  { valor: 'speed', rotulo: 'Speed' },
  { valor: 'turbo', rotulo: 'Turbo' },
  { valor: 'full_speed', rotulo: 'Full Speed (inclui Fiscal / NFC-e)' }
];

const ROTULO_SITUACAO = {
  paga: 'Paga', atrasada: 'Atrasada', a_vencer: 'A vencer', em_andamento: 'Em andamento', sem_cobranca: 'Sem cobrança'
};

const dinheiro = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
// Datas 'AAAA-MM-DD' são tratadas como texto: new Date('2026-10-17') é meia-noite UTC e
// no Brasil viraria o dia anterior.
const dataBR = (v) => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '—');
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hojeISO = () => isoLocal(new Date());

function maisDias(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return isoLocal(d);
}

function mesPorExtenso(competencia) {
  const [a, m] = competencia.split('-').map(Number);
  return new Date(a, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

// '2026-08' -> '08/2026' (cabe na tabela estreita do painel).
const mesCurto = (competencia) => competencia.split('-').reverse().join('/');
const diaMes = (v) => (v ? String(v).slice(5, 10).split('-').reverse().join('/') : '—');

function formDoPlano(assinatura) {
  return {
    plano: assinatura?.plano || 'teste',
    valor_mensal: assinatura?.valor_mensal ?? '',
    percentual_comissao: assinatura?.percentual_comissao ?? '',
    dia_vencimento: assinatura?.dia_vencimento ?? 10,
    cobranca_inicio: assinatura?.cobranca_inicio || '',
    teste_ate: assinatura?.teste_ate || maisDias(14),
    observacao: assinatura?.observacao || ''
  };
}

// Painel lateral de um cliente: ativação (funil) e plano/cobrança.
function ClienteDetalheMaster({ cliente, onFechar, onMudou }) {
  const navigate = useNavigate();
  const [aba, setAba] = useState('ativacao');
  const [cobranca, setCobranca] = useState(null);
  const [form, setForm] = useState(formDoPlano(null));
  const [ocupado, setOcupado] = useState(false);
  const [mensagem, setMensagem] = useState(null);
  const [pagando, setPagando] = useState(null); // { competencia, valor, data }

  const chamar = useCallback(async (caminho, opcoes = {}) => {
    const res = await fetchWithAuth(`${API_URL}/master/clientes/${cliente.id}${caminho}`, {
      ...opcoes,
      headers: { 'Content-Type': 'application/json', ...(opcoes.headers || {}) }
    });
    const corpo = res ? await res.json().catch(() => ({})) : {};
    if (!res || !res.ok) throw new Error(corpo.error || 'Falha na requisição');
    return corpo;
  }, [cliente.id]);

  const aplicar = useCallback((dados) => {
    setCobranca(dados);
    setForm(formDoPlano(dados.assinatura));
  }, []);

  useEffect(() => {
    let vivo = true;
    chamar('/cobranca').then((d) => { if (vivo) aplicar(d); }).catch((e) => { if (vivo) setMensagem({ tipo: 'erro', texto: e.message }); });
    return () => { vivo = false; };
  }, [chamar, aplicar]);

  const executar = async (fn, sucesso) => {
    setOcupado(true);
    setMensagem(null);
    try {
      aplicar(await fn());
      if (sucesso) setMensagem({ tipo: 'ok', texto: sucesso });
      onMudou();
    } catch (e) {
      setMensagem({ tipo: 'erro', texto: e.message });
    } finally {
      setOcupado(false);
    }
  };

  const salvarPlano = () => executar(() => chamar('/plano', {
    method: 'PUT',
    body: JSON.stringify({
      ...form,
      valor_mensal: form.valor_mensal === '' ? 0 : Number(form.valor_mensal),
      percentual_comissao: form.percentual_comissao === '' ? 0 : Number(form.percentual_comissao),
      dia_vencimento: Number(form.dia_vencimento),
      cobranca_inicio: form.cobranca_inicio || null,
      teste_ate: form.plano === 'teste' ? form.teste_ate : null
    })
  }), 'Plano salvo.');

  const salvarPlanoProduto = (plano_atual) => executar(
    () => chamar('/plano-atual', { method: 'PUT', body: JSON.stringify({ plano_atual }) }),
    'Plano do produto atualizado.'
  );

  const confirmarPagamento = () => executar(async () => {
    const dados = await chamar('/pagamentos', {
      method: 'POST',
      body: JSON.stringify({ competencia: pagando.competencia, valor_pago: Number(pagando.valor), pago_em: pagando.data })
    });
    setPagando(null);
    return dados;
  }, 'Pagamento registrado.');

  const desfazerPagamento = (competencia) => {
    if (!window.confirm(`Desfazer o pagamento de ${mesPorExtenso(competencia)}?`)) return;
    executar(() => chamar(`/pagamentos/${competencia}`, { method: 'DELETE' }), 'Pagamento desfeito.');
  };

  const planoAtual = PLANOS.find((p) => p.valor === form.plano);
  const mostraMensal = ['mensal', 'misto'].includes(form.plano);
  const mostraComissao = ['comissao', 'misto'].includes(form.plano);

  return (
    <div className={styles.fundo} onClick={onFechar}>
      <aside className={styles.painel} onClick={(e) => e.stopPropagation()}>
        <header className={styles.cabecalho}>
          <div>
            <h3>{cliente.nome}</h3>
            <p>{[cliente.segmento, cliente.nome_admin].filter(Boolean).join(' · ') || 'Cliente'}</p>
          </div>
          <button className={styles.fechar} onClick={onFechar} aria-label="Fechar"><MdClose /></button>
        </header>

        <nav className={styles.abas}>
          <button className={aba === 'ativacao' ? styles.abaAtiva : ''} onClick={() => setAba('ativacao')}>Ativação e saúde</button>
          <button className={aba === 'plano' ? styles.abaAtiva : ''} onClick={() => setAba('plano')}>Plano e cobrança</button>
        </nav>

        <div className={styles.corpo}>
          {aba === 'ativacao' && (
            <>
              <section className={styles.bloco}>
                <h4>Funil de ativação · {cliente.funil.percentual}%</h4>
                <ul className={styles.passos}>
                  {cliente.funil.passos.map((p) => (
                    <li key={p.id} className={p.feito ? styles.feito : styles.pendente}>
                      {p.feito ? <MdCheckCircle /> : <MdRadioButtonUnchecked />} {p.rotulo}
                    </li>
                  ))}
                </ul>
                {cliente.funil.proximo_passo && <p className={styles.dica}>Próximo passo: <strong>{cliente.funil.proximo_passo}</strong>.</p>}
              </section>

              <section className={styles.bloco}>
                <h4>Saúde</h4>
                {cliente.saude.motivos.length === 0
                  ? <p className={styles.ok}>Tudo certo: pedidos recentes e configuração completa.</p>
                  : <ul className={styles.motivos}>{cliente.saude.motivos.map((m) => <li key={m}>{m}</li>)}</ul>}
                <dl className={styles.numeros}>
                  <div><dt>Pedidos (7 dias)</dt><dd>{cliente.metricas.pedidos_7d}</dd></div>
                  <div><dt>Pedidos (30 dias)</dt><dd>{cliente.metricas.pedidos_30d}</dd></div>
                  <div><dt>Faturamento (30 dias)</dt><dd>{dinheiro(cliente.metricas.faturamento_30d)}</dd></div>
                  <div><dt>Produtos / Mesas / Equipe</dt><dd>{cliente.contagens.itens} / {cliente.contagens.mesas} / {cliente.contagens.equipe}</dd></div>
                </dl>
              </section>

              <section className={styles.bloco}>
                <h4>Contato</h4>
                <p className={styles.contato}>
                  {cliente.contato.telefone && <a href={`tel:${cliente.contato.telefone}`}>{cliente.contato.telefone}</a>}
                  {cliente.contato.email && <a href={`mailto:${cliente.contato.email}`}>{cliente.contato.email}</a>}
                </p>
                <button className={styles.btnSecundario} onClick={() => navigate(`/speedhub/estabelecimento/${cliente.id}`)}>Gerenciar estabelecimento</button>
              </section>
            </>
          )}

          {aba === 'plano' && (
            <>
              <section className={styles.bloco}>
                <h4>Plano do produto</h4>
                <label className={styles.campo}>
                  <span>O que o cliente pode usar</span>
                  <select
                    value={cobranca?.plano_atual || cliente.plano_atual || 'start'}
                    disabled={ocupado || !cobranca}
                    onChange={(e) => salvarPlanoProduto(e.target.value)}
                  >
                    {PLANOS_PRODUTO.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
                  </select>
                  <small>Só o Full Speed libera o Fiscal. Ao sair do Full Speed, a emissão automática é desligada.</small>
                </label>
              </section>

              <section className={styles.bloco}>
                <h4>Plano {cobranca && <span className={`${styles.pill} ${styles[`com_${cobranca.situacao.codigo}`]}`}>{cobranca.situacao.rotulo}</span>}</h4>

                <div className={styles.atalhos}>
                  <button onClick={() => setForm({ ...formDoPlano(null), plano: 'teste', teste_ate: maisDias(14) })}>Teste de 14 dias</button>
                  <button onClick={() => setForm({ ...formDoPlano(null), plano: 'teste', teste_ate: maisDias(30) })}>Teste de 30 dias</button>
                </div>

                <label className={styles.campo}>
                  <span>Tipo de plano</span>
                  <select value={form.plano} onChange={(e) => setForm({ ...form, plano: e.target.value })}>
                    {PLANOS.map((p) => (
                      // Comissão desabilitada por enquanto; um cliente que já a tem continua vendo o valor atual.
                      <option key={p.valor} value={p.valor} disabled={p.desabilitado && form.plano !== p.valor}>{p.rotulo}</option>
                    ))}
                  </select>
                  <small>{planoAtual?.ajuda}</small>
                </label>

                {form.plano === 'teste' && (
                  <label className={styles.campo}><span>Teste vai até</span>
                    <input type="date" value={form.teste_ate} onChange={(e) => setForm({ ...form, teste_ate: e.target.value })} />
                  </label>
                )}
                {mostraMensal && (
                  <label className={styles.campo}><span>Valor mensal (R$)</span>
                    <input type="number" min="0" step="0.01" value={form.valor_mensal} onChange={(e) => setForm({ ...form, valor_mensal: e.target.value })} />
                  </label>
                )}
                {mostraComissao && (
                  <label className={styles.campo}><span>Comissão sobre o faturamento (%)</span>
                    <input type="number" min="0" max="100" step="0.1" value={form.percentual_comissao} onChange={(e) => setForm({ ...form, percentual_comissao: e.target.value })} />
                  </label>
                )}
                {!['teste', 'isento'].includes(form.plano) && (
                  <div className={styles.duas}>
                    <label className={styles.campo}><span>Vence todo dia</span>
                      <input type="number" min="1" max="28" value={form.dia_vencimento} onChange={(e) => setForm({ ...form, dia_vencimento: e.target.value })} />
                      <small>do mês seguinte ao uso</small>
                    </label>
                    <label className={styles.campo}><span>Cobrar a partir de</span>
                      <input type="date" value={form.cobranca_inicio} onChange={(e) => setForm({ ...form, cobranca_inicio: e.target.value })} />
                      <small>vazio = mês atual</small>
                    </label>
                  </div>
                )}
                <label className={styles.campo}><span>Observação</span>
                  <input value={form.observacao} maxLength={255} onChange={(e) => setForm({ ...form, observacao: e.target.value })} placeholder="Ex.: acordado por WhatsApp em 10/10" />
                </label>

                <button className={styles.btnPrimario} onClick={salvarPlano} disabled={ocupado}>{ocupado ? 'Salvando...' : 'Salvar plano'}</button>
              </section>

              {cobranca?.extrato.length > 0 && (
                <section className={styles.bloco}>
                  <h4>Cobranças por mês</h4>
                  <table className={styles.extrato}>
                    <thead><tr><th>Mês</th><th>Faturou</th><th>Cobrar</th><th>Vence</th><th>Situação</th><th /></tr></thead>
                    <tbody>
                      {cobranca.extrato.map((e) => (
                        <tr key={e.competencia}>
                          <td>{mesCurto(e.competencia)}</td>
                          <td>{dinheiro(e.faturamento)}</td>
                          <td>
                            {dinheiro(e.total)}
                            {e.mensalidade > 0 && e.comissao > 0 && <small className={styles.quebra}>{dinheiro(e.mensalidade)} + {dinheiro(e.comissao)}</small>}
                          </td>
                          <td>{diaMes(e.vencimento)}</td>
                          <td><span className={`${styles.pill} ${styles[`ext_${e.situacao}`]}`}>{ROTULO_SITUACAO[e.situacao]}</span>
                            {e.pagamento && <small className={styles.quebra}>em {dataBR(e.pagamento.pago_em)}</small>}
                          </td>
                          <td className={styles.acao}>
                            {e.pagamento
                              ? <button onClick={() => desfazerPagamento(e.competencia)} disabled={ocupado}>Desfazer</button>
                              : ['atrasada', 'a_vencer'].includes(e.situacao) && (
                                <button onClick={() => setPagando({ competencia: e.competencia, valor: e.total.toFixed(2), data: hojeISO() })} disabled={ocupado}>Dar baixa</button>
                              )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {pagando && (
                    <div className={styles.pagando}>
                      <strong>Pagamento de {mesPorExtenso(pagando.competencia)}</strong>
                      <label className={styles.campo}><span>Valor recebido (R$)</span>
                        <input type="number" min="0" step="0.01" value={pagando.valor} onChange={(e) => setPagando({ ...pagando, valor: e.target.value })} />
                      </label>
                      <label className={styles.campo}><span>Data do pagamento</span>
                        <input type="date" value={pagando.data} onChange={(e) => setPagando({ ...pagando, data: e.target.value })} />
                      </label>
                      <div className={styles.acoesLinha}>
                        <button className={styles.btnPrimario} onClick={confirmarPagamento} disabled={ocupado}>Confirmar</button>
                        <button className={styles.btnSecundario} onClick={() => setPagando(null)}>Cancelar</button>
                      </div>
                    </div>
                  )}
                  <p className={styles.dica}>O pagamento é registrado à mão: o sistema ainda não cobra nem recebe sozinho.</p>
                </section>
              )}
            </>
          )}

          {mensagem && <p className={mensagem.tipo === 'ok' ? styles.msgOk : styles.msgErro}>{mensagem.texto}</p>}
        </div>
      </aside>
    </div>
  );
}

export default ClienteDetalheMaster;
